// Interval bucketing for the analyzer spend chart.
//
// The chart fetches DAILY rows and clips them to the selected period. Everything
// here is exact client-side summation over those rows, so Week and Month cost no
// extra request — the same property cumulative.js has.
//
// Bucket keys are date STRINGS throughout, so no timezone ever enters the
// computation: a week is its Monday, a month is its first day.

import dayjs from "dayjs";

// normalizeDay puts both date shapes the chart ever sees onto one ISO timeline: the
// backend emits compact "YYYYMMDD" for spend rows but ISO "YYYY-MM-DD" for forecast
// points, and sorting the raw strings interleaves them wrong (dashes sort before
// digits). Anything else is passed through unchanged rather than guessed at.
//
// It lives here rather than in AnalyzerChart because bucketing needs it and the
// chart imports this module — the other direction would be a cycle.
export function normalizeDay(s) {
  if (typeof s !== "string") return s;
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
  return s;
}

// autoInterval is the "Auto" granularity rule: enough resolution to see what the
// range can actually show, without drawing 260 daily points for a YTD selection.
// Kept pure (rather than inside useAnalyzerScope) so its boundaries are testable
// without rendering a hook.
export function autoInterval(start, end) {
  if (!start || !end) return "day";
  const a = dayjs(start);
  const b = dayjs(end);
  if (!a.isValid() || !b.isValid()) return "day";
  const days = b.diff(a, "day") + 1; // inclusive
  if (days <= 45) return "day";
  if (days <= 180) return "week";
  return "month";
}

// bucketKey maps one ISO day onto the key of the bucket containing it.
// Weeks start MONDAY (dayjs day(): 0=Sun..6=Sat), so Sunday belongs to the week
// that began six days earlier rather than opening a new one.
export function bucketKey(isoDate, interval) {
  if (interval === "day" || !isoDate) return isoDate;
  const d = dayjs(normalizeDay(isoDate));
  if (!d.isValid()) return isoDate;
  if (interval === "month") return d.format("YYYY-MM-01");
  const dow = d.day();
  const back = dow === 0 ? 6 : dow - 1;
  return d.subtract(back, "day").format("YYYY-MM-DD");
}

// bucketEnd is the last calendar day the bucket spans.
export function bucketEnd(key, interval) {
  if (interval === "day") return key;
  const d = dayjs(key);
  if (!d.isValid()) return key;
  if (interval === "month") return d.endOf("month").format("YYYY-MM-DD");
  return d.add(6, "day").format("YYYY-MM-DD");
}

// calendarDays is how many days the bucket spans on the calendar — 30 for
// September, 29 for February 2024 — regardless of how much data covers it.
export function calendarDays(key, interval) {
  if (interval === "day") return 1;
  if (interval === "week") return 7;
  const d = dayjs(key);
  return d.isValid() ? d.daysInMonth() : 0;
}

// coveredDays is how many of the bucket's calendar days fall inside [lo, hi] —
// the selection intersected with the days data actually reaches. Fewer than
// calendarDays means the bucket is PARTIAL, which happens at BOTH ends of a
// range: Jul 15 – Sep 15 at Month yields a partial July and a partial September.
export function coveredDays(key, interval, lo, hi) {
  const s = lo && key < lo ? lo : key;
  const rawEnd = bucketEnd(key, interval);
  const e = hi && rawEnd > hi ? hi : rawEnd;
  if (!s || !e || e < s) return 0;
  const a = dayjs(s);
  const b = dayjs(e);
  if (!a.isValid() || !b.isValid()) return 0;
  return b.diff(a, "day") + 1;
}

// windowOf derives the [lo, hi] day window a bucket's coverage is measured
// against: the selected period, further clipped to the last day the data
// actually reaches. That second clip is what makes an Invoiced-basis September
// with no forecast read as partial, while a September carried to month end by a
// forecast does not.
function windowOf(days, from, to) {
  const sorted = [...days].sort();
  const firstDay = sorted[0];
  const lastDay = sorted[sorted.length - 1];
  const lo = from && from > firstDay ? from : firstDay;
  const capped = to && to < lastDay ? to : lastDay;
  return { lo, hi: capped };
}

// withCoverage stamps the partial-bucket metadata onto a bucketed row.
function withCoverage(row, key, interval, lo, hi) {
  const cal = calendarDays(key, interval);
  const covered = coveredDays(key, interval, lo, hi);
  return { ...row, __bucketDays: covered, __calendarDays: cal, __partial: covered < cal };
}

// bucketSingle aggregates the single-series rows (mergeSingle's output) into one
// row per bucket.
//
// THE DOUBLE-COUNT RULE: mergeSingle puts `actual` AND `forecast` on the same row
// at the handover day. Summing both into one month would count that day twice, so
// on a day carrying both the ACTUAL WINS and the forecast is dropped. It is resolved
// HERE, once — which is precisely why the bucket that comes out must NOT have it
// applied again: a bucket carrying both `actual` and `forecast` holds two DISJOINT
// sums (the actual days, plus the forward days this rule left untouched), so the
// cumulative pass adds them rather than anchoring one on the other. See the
// `disjoint` flag documented at the top of cumulative.js.
//
// Band bounds are summed within the bucket, inheriting the conservative
// assumption cumulative.js documents — daily errors treated as perfectly
// correlated, so the range reads WIDER than a properly-derived one. Narrowing it
// is a forecasting decision on the backend, not a charting one.
export function bucketSingle(rows, interval, from, to) {
  if (interval === "day") return rows;
  const src = rows || [];
  if (!src.length) return [];

  const { lo, hi } = windowOf(src.map((r) => r.date).filter(Boolean), from, to);
  const byKey = new Map();

  for (const r of src) {
    const key = bucketKey(r.date, interval);
    if (!byKey.has(key)) byKey.set(key, { date: key });
    const b = byKey.get(key);
    if (r.actual != null) {
      b.actual = (b.actual || 0) + (Number(r.actual) || 0);
    } else if (r.forecast != null) {
      b.forecast = (b.forecast || 0) + (Number(r.forecast) || 0);
      if (r.lower != null) b.lower = (b.lower || 0) + (Number(r.lower) || 0);
      if (r.upper != null) b.upper = (b.upper || 0) + (Number(r.upper) || 0);
    }
  }

  return [...byKey.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, row]) => {
      const out = withCoverage(row, key, interval, lo, hi);
      if (out.lower != null && out.upper != null) out.band = [out.lower, out.upper];
      return out;
    });
}

// completeMeta recomputes the run-rate average and the peak over COMPLETE buckets
// only. A half-landed September counted as a whole month drags "Avg €X/month"
// down and makes the reference line lie about the run rate. Rows with no
// __partial flag (i.e. day granularity, where bucketing is a no-op) count as
// complete, so daily behaviour is unchanged.
export function completeMeta(rows) {
  const complete = (rows || []).filter((r) => r.__partial !== true && r.actual != null);
  if (!complete.length) return { avg: null, peak: null };
  const avg = complete.reduce((s, r) => s + (Number(r.actual) || 0), 0) / complete.length;
  let peak = null;
  for (const r of complete) if (peak == null || r.actual > peak.actual) peak = r;
  return { avg, peak };
}

// bucketStacked aggregates the wide breakdown rows into one row per bucket.
// `keys` is every numeric column to sum — the caller passes the actual stack keys
// AND their forward `<group>__forecast` counterparts, so both sides of the
// handover survive bucketing with their stack order intact.
//
// A key absent from every row in a bucket stays ABSENT rather than becoming 0:
// pivotStacked already densified the real layers, so an undefined here means the
// forward side genuinely has no such layer, and inventing a 0 would draw it.
export function bucketStacked(rows, keys, interval, from, to) {
  if (interval === "day") return rows;
  const src = rows || [];
  if (!src.length) return [];

  const { lo, hi } = windowOf(src.map((r) => r.time).filter(Boolean), from, to);
  const cols = keys || [];
  const byKey = new Map();

  for (const r of src) {
    const key = bucketKey(r.time, interval);
    if (!byKey.has(key)) byKey.set(key, { time: key });
    const b = byKey.get(key);
    for (const k of cols) {
      if (r[k] == null) continue;
      b[k] = (b[k] || 0) + (Number(r[k]) || 0);
    }
    if (r.__total != null) b.__total = (b.__total || 0) + (Number(r.__total) || 0);
    if (Array.isArray(r.__band)) {
      const cur = b.__band || [0, 0];
      b.__band = [cur[0] + (Number(r.__band[0]) || 0), cur[1] + (Number(r.__band[1]) || 0)];
    }
  }

  return [...byKey.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, row]) => withCoverage(row, key, interval, lo, hi));
}

// bucketMeasures aggregates the { time, measure } overlays — the comparison ghost
// and the API-equivalent shadow. The ghost is aligned to the main series BY INDEX
// (day-1-over-day-1), so bucketing it against its OWN dates is what turns that
// alignment into bucket-1-over-bucket-1: "Sep vs Aug" compares whole months
// rather than offset day windows.
export function bucketMeasures(points, interval) {
  if (interval === "day") return points;
  const src = points || [];
  const byKey = new Map();
  for (const p of src) {
    // Normalize belt-and-braces: this function's inputs are raw API points (comparison ghost,
    // API-equivalent shadow), unlike bucketStacked's pre-normalized rows. bucketKey normalizes
    // internally too, but an explicit call here protects against coupling to that detail.
    const key = bucketKey(normalizeDay(p.time), interval);
    byKey.set(key, (byKey.get(key) || 0) + (Number(p.measure) || 0));
  }
  return [...byKey.entries()]
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    .map(([time, measure]) => ({ time, measure }));
}

// completeStackedMeta is stackedMeta's partial-aware sibling: the average and peak
// come from COMPLETE buckets only, while the first/last time and last row describe
// everything DRAWN — the subtitle's date range must match the axis, not the
// average's sample.
export function completeStackedMeta(rows, stackKeys) {
  const data = rows || [];
  if (!data.length) return { firstTime: null, lastTime: null, avgTotal: null, peak: null, lastRow: null };
  const keys = stackKeys || [];
  const totalOf = (r) => keys.reduce((s, k) => s + (Number(r[k]) || 0), 0);

  const complete = data.filter((r) => r.__partial !== true);
  let sum = 0;
  let peak = null;
  for (const r of complete) {
    const total = totalOf(r);
    sum += total;
    if (peak == null || total > peak.total) {
      let driver = null;
      let dv = -Infinity;
      for (const k of keys) { const v = Number(r[k]) || 0; if (v > dv) { dv = v; driver = k; } }
      peak = { time: r.time, total, driver };
    }
  }

  return {
    firstTime: data[0].time,
    lastTime: data[data.length - 1].time,
    avgTotal: complete.length ? sum / complete.length : null,
    peak,
    lastRow: data[data.length - 1],
  };
}

// toBarSegments derives the two stacked bar segments from a bucketed row, so the
// bar's total height is always the projected period total and never double-counts.
//
// Period mode: the double-count rule already dropped the handover day's forecast,
// so `actual` and `forecast` are disjoint sums and stack directly.
//
// Cumulative mode: `forecast` is the cumulative PROJECTED total, which already
// CONTAINS the actual portion — so the top segment must be the delta, and a
// forecast-only bucket takes the last cumulative actual as its base.
export function toBarSegments(rows, cumulative) {
  let lastActual = 0;
  return (rows || []).map((r) => {
    const actual = r.actual != null ? Number(r.actual) || 0 : null;
    const forecast = r.forecast != null ? Number(r.forecast) || 0 : null;
    if (!cumulative) {
      return { ...r, __barActual: actual ?? 0, __barForecast: forecast ?? 0 };
    }
    const base = actual != null ? actual : lastActual;
    if (actual != null) lastActual = actual;
    return {
      ...r,
      __barActual: base,
      __barForecast: forecast != null ? Math.max(0, forecast - base) : 0,
    };
  });
}

// intervalNoun is the denominator the run-rate reference line quotes:
// "Avg €120/day" vs "Avg €3.4k/month".
export function intervalNoun(interval) {
  if (interval === "month") return "month";
  if (interval === "week") return "week";
  return "day";
}

// bucketTickLabel is the x-axis tick. Month ticks carry the year only once the
// selection crosses one, so a within-year range stays uncluttered.
export function bucketTickLabel(key, interval, spansYears) {
  const d = dayjs(key);
  if (!d.isValid()) return key;
  if (interval === "month") return spansYears ? d.format("MMM 'YY") : d.format("MMM");
  return d.format("MMM D");
}

// bucketRangeLabel is the tooltip heading — it must name the whole span the
// hovered bar covers, not just its first day.
export function bucketRangeLabel(key, interval) {
  const d = dayjs(key);
  if (!d.isValid()) return key;
  if (interval === "month") return d.format("MMMM YYYY");
  if (interval === "week") {
    const e = dayjs(bucketEnd(key, interval));
    const endFmt = e.month() === d.month() ? e.format("D") : e.format("MMM D");
    const startFmt = d.year() !== e.year()
      ? `${d.format("MMM D, YYYY")} – ${endFmt}, ${e.format("YYYY")}`
      : `${d.format("MMM D")} – ${endFmt}, ${e.format("YYYY")}`;
    return startFmt;
  }
  return d.format("MMM D, YYYY");
}

// chartTitleFor names BOTH controls, so neither the Auto pick nor the cumulative
// setting is ever hidden from the reader.
export function chartTitleFor({ interval, cumulative, isSingleSeries, groupByLabel }) {
  const adj = interval === "month" ? "monthly" : interval === "week" ? "weekly" : "daily";
  const head = cumulative
    ? `Cumulative ${adj} spend`
    : `${adj.charAt(0).toUpperCase()}${adj.slice(1)} spend`;
  return isSingleSeries ? head : `${head} by ${groupByLabel || "cost source"}`;
}
